import { getActiveContext } from '@/lib/active-context-store';
import { apiClient as client } from '@/lib/api-client';
import type {
  EditedText,
  JourneysReply,
  LogFilters,
  LogPage,
  LogQuery,
  OnboardingMessage,
  PendingPage,
} from './api-types';

export type Outcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

async function call<T>(
  run: () => Promise<{ data?: unknown; error?: unknown; response: Response }>,
): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok) return { ok: true, value: data as T };
    const code =
      typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
    return { ok: false, status: response.status, code };
  } catch {
    return { ok: false, status: 0, code: '' };
  }
}

/**
 * The generated types of the context-bound routes declare the active-context headers; the client
 * fills them from the same store, so this only satisfies the types with the same values.
 */
function active() {
  const context = getActiveContext();
  return {
    'X-Active-Tenant-Id': context?.tenantId ?? '',
    'X-Active-Organization-Id': context?.organizationId ?? '',
  };
}

const withId = (id: string) => ({ params: { path: { id }, header: active() } });

export const listApprovals = (cursor?: string) =>
  call<PendingPage>(() =>
    client.GET('/v1/communications/pending', {
      params: { query: cursor === undefined ? {} : { cursor }, header: active() },
    }),
  );

export const approve = (id: string, edited?: EditedText) =>
  call<OnboardingMessage>(() =>
    client.POST('/v1/communications/messages/{id}/approve', {
      ...withId(id),
      body: edited ? { subject: edited.subject, bodyText: edited.bodyText } : {},
    }),
  );

export const postpone = (id: string) =>
  call<OnboardingMessage>(() =>
    client.POST('/v1/communications/messages/{id}/postpone', withId(id)),
  );

export const discard = (id: string) =>
  call<OnboardingMessage>(() =>
    client.POST('/v1/communications/messages/{id}/discard', withId(id)),
  );

/** Only the filters that hold a value go on the wire. */
function clean<T extends object>(filters: T): T {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
  ) as T;
}

export const listLog = (query: LogQuery) =>
  call<LogPage>(() =>
    client.GET('/v1/communications/log', {
      params: { query: clean(query), header: active() },
    }),
  );

/** The log as a CSV file, with the filters in force, handed to the browser to save. */
export async function downloadLog(filters: LogFilters): Promise<boolean> {
  const answer = await call<Blob>(() =>
    client.GET('/v1/communications/log.csv', {
      params: { query: clean(filters), header: active() },
      parseAs: 'blob',
    }),
  );
  if (!answer.ok) return false;
  const url = URL.createObjectURL(answer.value);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'comunicazioni-registro.csv';
  anchor.click();
  URL.revokeObjectURL(url);
  return true;
}

export const getJourney = () => call<JourneysReply>(() => client.GET('/v1/profile/communications'));

export const stopJourney = () =>
  call<JourneysReply>(() => client.POST('/v1/profile/communications/stop'));

export const resumeJourney = () =>
  call<JourneysReply>(() => client.POST('/v1/profile/communications/resume'));

export const changePath = (journey: string) =>
  call<JourneysReply>(() =>
    client.POST('/v1/profile/communications/path', {
      body: { journey },
    }),
  );

export const logClick = (step: string) =>
  call<JourneysReply>(() =>
    client.POST('/v1/profile/communications/clicks', {
      body: { step },
    }),
  );

/**
 * The caller's permissions in «Comunicazioni». The active context comes from the caller, not from
 * the module-level store: the provider syncs that store in an effect that runs AFTER this hook's own
 * effect on a reload, so reading it here sent empty `X-Active-*` headers (a 400), the hook failed
 * closed and the rail entry vanished until the next change of context.
 */
export const readPermissions = (tenantId: string, organizationId: string) =>
  call<{ permissions?: string[] }>(() =>
    client.GET('/v1/access/my-permissions', {
      params: {
        query: { module: 'communications' },
        header: { 'X-Active-Tenant-Id': tenantId, 'X-Active-Organization-Id': organizationId },
      },
    }),
  );

/**
 * The public unsubscribe: no sign-in, so no client; the link's token is the proof. The contract
 * answers 200 for ANY token (never an oracle), so only a network or server failure is an error.
 */
export async function unsubscribe(token: string): Promise<'done' | 'error'> {
  try {
    const base = import.meta.env.VITE_API_BASE_URL ?? '';
    const response = await fetch(`${base}/v1/onboarding/unsubscribe/${encodeURIComponent(token)}`, {
      method: 'POST',
    });
    return response.ok ? 'done' : 'error';
  } catch {
    return 'error';
  }
}
